import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Paper, TextField, InputAdornment, IconButton, Button,
  Chip, Snackbar, Alert, Skeleton, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from '@mui/material';
import { Add, Kitchen, Search, Check } from '@mui/icons-material';
import { useRecipes } from '../../contexts/RecipeContext';

export default function PantryPage() {
  const { pantry, pantryLoading, updatePantry } = useRecipes();
  const [newItem, setNewItem] = useState('');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [clearConfirm, setClearConfirm] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const items = useMemo(() => pantry?.items || [], [pantry]);

  const filteredItems = useMemo(() => {
    if (!search) return items;
    return items.filter((item) => item.name.toLowerCase().includes(search.toLowerCase()));
  }, [items, search]);

  const inStockItems = filteredItems.filter((i) => i.inStock);
  const outOfStockItems = filteredItems.filter((i) => !i.inStock);

  const save = async (newItems) => {
    setSaving(true);
    try {
      await updatePantry(newItems);
    } catch (err) {
      showSnackbar('Failed to save pantry', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = async () => {
    const name = newItem.trim();
    if (!name) return;
    if (items.some((i) => i.name.toLowerCase() === name.toLowerCase())) {
      showSnackbar('Already in pantry', 'warning');
      return;
    }
    setNewItem('');
    await save([...items, { id: Date.now().toString(), name, inStock: true }]);
    showSnackbar(`"${name}" added`);
  };

  const handleToggle = async (id) => {
    await save(items.map((i) => i.id === id ? { ...i, inStock: !i.inStock } : i));
  };

  const handleDelete = async (id) => {
    await save(items.filter((i) => i.id !== id));
  };

  const handleMarkAllInStock = async () => {
    await save(items.map((i) => ({ ...i, inStock: true })));
    showSnackbar('All marked as in stock');
  };

  const handleClearAll = async () => {
    await save([]);
    setClearConfirm(false);
    showSnackbar('Pantry cleared');
  };

  if (pantryLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={200} height={40} />
        <Skeleton variant="rounded" height={56} sx={{ mt: 2 }} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3 }}>
          {[1,2,3,4,5,6,7,8].map((i) => <Skeleton key={i} variant="rounded" width={90} height={32} sx={{ borderRadius: 4 }} />)}
        </Box>
      </Box>
    );
  }

  const IngredientChip = ({ item }) => (
    <Chip
      label={item.name}
      onClick={() => handleToggle(item.id)}
      onDelete={() => handleDelete(item.id)}
      icon={item.inStock ? <Check sx={{ fontSize: '14px !important' }} /> : undefined}
      sx={{
        borderRadius: 4,
        fontWeight: 500,
        transition: 'all 0.15s',
        ...(item.inStock ? {
          bgcolor: 'rgba(107,176,122,0.15)',
          color: 'success.main',
          border: '1px solid rgba(107,176,122,0.35)',
          '&:hover': { bgcolor: 'rgba(107,176,122,0.25)' },
          '& .MuiChip-deleteIcon': { color: 'success.main', opacity: 0.6, '&:hover': { opacity: 1 } },
        } : {
          bgcolor: 'rgba(255,255,255,0.04)',
          color: 'text.secondary',
          border: '1px solid rgba(255,255,255,0.1)',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
          '& .MuiChip-deleteIcon': { opacity: 0.5, '&:hover': { opacity: 0.9 } },
        }),
      }}
    />
  );

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5">My Pantry</Typography>
          <Typography variant="body2" color="text.secondary">
            {items.filter(i => i.inStock).length} in stock · {items.length} total
          </Typography>
        </Box>
        {saving && <CircularProgress size={20} sx={{ color: 'primary.main' }} />}
      </Box>

      {/* Add item */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <TextField
          placeholder="Add ingredient (e.g. olive oil, garlic)..."
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
          fullWidth
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Kitchen sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" onClick={handleAddItem} disabled={!newItem.trim()}
          sx={{ flexShrink: 0, background: 'linear-gradient(135deg, #f0873c, #c96820)' }}>
          <Add />
        </Button>
      </Box>

      {/* Search */}
      {items.length > 6 && (
        <TextField
          placeholder="Search pantry..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth size="small"
          sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
      )}

      {items.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '3rem', mb: 2 }}>🧺</Typography>
          <Typography variant="h6" sx={{ mb: 1 }}>Your pantry is empty</Typography>
          <Typography color="text.secondary">
            Add ingredients you have at home to see what you can cook.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Bulk actions */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <Button size="small" onClick={handleMarkAllInStock}
              sx={{ color: 'success.main', borderColor: 'rgba(107,176,122,0.3)', border: '1px solid', borderRadius: 2, px: 1.5 }}>
              All in stock
            </Button>
            <Button size="small" onClick={() => setClearConfirm(true)}
              sx={{ color: 'error.main', borderColor: 'rgba(239,83,80,0.25)', border: '1px solid', borderRadius: 2, px: 1.5 }}>
              Clear all
            </Button>
          </Box>

          {/* In Stock chips */}
          {inStockItems.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="subtitle2" color="success.main" fontWeight={600}>In Stock</Typography>
                <Chip label={inStockItems.length} size="small"
                  sx={{ bgcolor: 'rgba(102,187,106,0.15)', color: 'success.main', height: 20, fontSize: '0.7rem' }} />
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {inStockItems.map((item) => <IngredientChip key={item.id} item={item} />)}
              </Box>
            </Box>
          )}

          {/* Out of Stock chips */}
          {outOfStockItems.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>Out of Stock</Typography>
                <Chip label={outOfStockItems.length} size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.05)', height: 20, fontSize: '0.7rem' }} />
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {outOfStockItems.map((item) => <IngredientChip key={item.id} item={item} />)}
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Clear confirm */}
      <Dialog open={clearConfirm} onClose={() => setClearConfirm(false)}>
        <DialogTitle>Clear pantry?</DialogTitle>
        <DialogContent>
          <Typography>This will remove all {items.length} ingredients from your pantry.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearConfirm(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleClearAll}>Clear All</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
